import React, { useState, useEffect } from "react";
import { CARD_BG, SHADOW } from "../config/theme";
import { useNavigate } from "react-router-dom";
import {
  getAllReports,
  deleteReport,
  getStorageStats,
  type SavedReport,
} from "../utils/reportManager";
import {
  getReportFamilyAdapter,
  getReportHistorySummary,
} from "../utils/reportFamilyAdapters";

interface ReportHistoryProps {
  currentReportId?: string;
  onReportSelect: (reportId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Report History Sidebar
 *
 * Displays a list of saved reports from LocalStorage, allowing users to:
 * - View all uploaded reports
 * - Switch between reports
 * - Delete old reports
 * - See storage statistics
 */
export const ReportHistory: React.FC<ReportHistoryProps> = ({
  currentReportId,
  onReportSelect,
  isOpen,
  onToggle,
}) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [stats, setStats] = useState(getStorageStats());

  const loadReports = () => {
    const allReports = getAllReports();
    setReports(allReports);
    setStats(getStorageStats());
  };

  useEffect(() => {
    loadReports();
  }, [currentReportId]);

  const handleDelete = (reportId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm("Delete this report?")) {
      deleteReport(reportId);
      loadReports();

      // If deleting current report, go to upload page
      if (reportId === currentReportId) {
        navigate("/upload");
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <>
      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 ${CARD_BG} ${SHADOW.sidebar} transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Report History
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {stats.reportCount} saved • {stats.estimatedSizeKB}KB
            </p>
          </div>
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-500 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Stats Summary */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-2">
            <div className={`text-center p-2 ${CARD_BG} rounded-lg`}>
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {stats.searchCount}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Search
              </div>
            </div>
            <div className={`text-center p-2 ${CARD_BG} rounded-lg`}>
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                {stats.onsiteCount + stats.offsiteCount}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Display
              </div>
            </div>
          </div>
          {stats.linearCount > 0 && (
            <div className="mt-2">
              <div className={`text-center p-2 ${CARD_BG} rounded-lg`}>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {stats.linearCount}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Linear TV
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reports List */}
        <div className="overflow-y-auto h-[calc(100%-200px)]">
          {reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <svg
                className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                No saved reports yet
              </p>
              <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">
                Upload a report to get started
              </p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {reports.map((report) => {
                const reportFamily = getReportFamilyAdapter(report.report_type);
                const summary = getReportHistorySummary(report);
                return (
                  <div
                    key={report.id}
                    onClick={() => {
                      onReportSelect(report.id);
                      onToggle();
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      report.id === currentReportId
                        ? `bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 ${SHADOW.card}`
                        : `${CARD_BG} border-gray-200 dark:border-[#5B5353] hover:border-gray-300 dark:hover:border-[#948A8B] ${SHADOW.cardHover}`
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${reportFamily.colorClasses}`}
                      >
                        {reportFamily.label}
                      </span>
                      <button
                        onClick={(e) => handleDelete(report.id, e)}
                        className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete report"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="text-sm">
                      <div className="font-semibold text-gray-900 dark:text-white mb-1">
                        {summary.primaryValue && summary.primaryLabel ? (
                          <>
                            $
                            {parseFloat(summary.primaryValue).toLocaleString(
                              "en-US",
                              {
                                maximumFractionDigits: 0,
                              },
                            )}{" "}
                            <span className="text-xs text-gray-500">
                              {summary.primaryLabel}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500">No data</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <div className="flex justify-between">
                          <span>{summary.detailLeft}</span>
                          <span>{summary.detailRight}</span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-500">
                          {report.date_range_start} - {report.date_range_end}
                        </div>
                        <div className="text-gray-400 dark:text-gray-600">
                          {formatDate(report.uploaded_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Reports auto-delete after 7 days
          </p>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}
    </>
  );
};
